import { type FC, useCallback, useEffect } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { SvgIconType } from '@thermenresorts/icons';
import type ApiRequestError from 'src/core/models/api/ApiRequestError';
import type ApiWorkspace from 'src/core/models/api/ApiWorkspace';
import Colors from 'src/core/utils/Colors';
import Dimens from 'src/core/utils/Dimens';
import Fonts from 'src/core/utils/Fonts';
import Translations from 'src/core/utils/Translations';
import { useServicesStore } from 'src/domain/stores/useServicesStore';
import { useWorkspacesStore } from 'src/domain/stores/useWorkspacesStore';
import SvgIcon from 'src/presentation/components/atoms/SvgIcon';
import LoadingIndicator from 'src/presentation/components/molecules/LoadingIndicator';
import ThermenImage from 'src/presentation/components/molecules/ThermenImage';
import BottomSheet from 'src/presentation/components/organisms/BottomSheet';
import ErrorView from 'src/presentation/components/organisms/ErrorView';
import LeaveAppDialog from 'src/presentation/dialogs/LeaveAppDialog';
import useBathCardActions from 'src/presentation/hooks/useBathCardActions';

interface SheetProps {
	onClose: () => void;
}

const OrderBathCardSheet: FC<SheetProps> = ({ onClose }: SheetProps) => {
	const { workspaces, workspacesLoading, workspacesErrorMessage, fetchWorkspaces, getPageTitle } = useWorkspacesStore();
	const { openBathCardOrder, openWorkspaceWebsite } = useBathCardActions();
	const { captureEvent } = useServicesStore();

	const load = useCallback(() => {
		fetchWorkspaces().catch((error: ApiRequestError) =>
			captureEvent({ message: 'fetchWorkspaces failed', extra: { error } }),
		);
	}, [captureEvent, fetchWorkspaces]);

	useEffect(() => {
		load();
	}, [load]);

	const onWorkspacePress = async (workspace: ApiWorkspace) => {
		if (!workspace.available_in_active_locale) {
			return openWorkspaceWebsite(workspace);
		}

		await openBathCardOrder(workspace);
	};

	const title = getPageTitle();

	return (
		<BottomSheet visible={true} onClose={onClose}>
			{title === undefined ? null : <Text style={styles.title}>{title}</Text>}

			{workspacesErrorMessage !== null ? (
				<ErrorView onPress={load} />
			) : workspacesLoading ? (
				<LoadingIndicator style={styles.loading} />
			) : workspaces.length === 0 ? (
				<ErrorView onPress={load} />
			) : (
				workspaces.map((workspace: ApiWorkspace) => (
					<Pressable
						key={workspace.id.toString()}
						style={({ pressed }) => [styles.card, { backgroundColor: pressed ? Colors.gray150 : Colors.gray75 }]}
						onPress={() => onWorkspacePress(workspace)}
					>
						<View style={styles.logoContainer}>
							<ThermenImage key={workspace.logo} source={{ uri: workspace.logo }} style={styles.logo} />
						</View>
						<View style={styles.cardText}>
							<Text style={styles.resort}>{workspace.title}</Text>
							{workspace.available_in_active_locale ? null : (
								<Text style={styles.unavailableMessage}>{Translations.unavailableInActiveLocaleMessage}</Text>
							)}
						</View>
						<View style={styles.chevronCircle}>
							<SvgIcon name={SvgIconType.chevronRight} size={Dimens.iconSize12} tintColor={Colors.gray600} />
						</View>
					</Pressable>
				))
			)}

			{/* The confirmation belongs to this modal's own tree, which is what lets iOS present it
			over the sheet instead of refusing a second modal. App.tsx steps aside while it is here. */}
			<LeaveAppDialog />
		</BottomSheet>
	);
};

const OrderBathCardDialog: FC = () => {
	const { isResortPickerVisible, closeResortPicker } = useWorkspacesStore();

	if (!isResortPickerVisible) {
		return null;
	}

	return <OrderBathCardSheet onClose={closeResortPicker} />;
};

const styles = StyleSheet.create({
	title: {
		fontFamily: Fonts.ptSerif,
		fontSize: Dimens.fontSize22,
		lineHeight: Dimens.lineHeight30,
		color: Colors.gray900,
	},
	loading: {
		paddingVertical: Dimens.padding24,
	},
	card: {
		flexDirection: 'row',
		padding: Dimens.padding16,
		borderRadius: Dimens.borderRadius6,
	},
	logoContainer: {
		paddingRight: Dimens.padding18,
		justifyContent: 'center',
		alignItems: 'center',
	},
	logo: {
		height: Dimens.size40,
		width: Dimens.size40,
	},
	cardText: {
		flexDirection: 'column',
		justifyContent: 'center',
		paddingRight: Dimens.padding20,
		flex: Dimens.flex1,
	},
	resort: {
		fontSize: Dimens.fontSize16,
		lineHeight: Dimens.lineHeight24,
		fontFamily: Fonts.ptSerif,
		color: Colors.gray900,
	},
	unavailableMessage: {
		fontSize: Dimens.fontSize12,
		color: Colors.gray550,
	},
	chevronCircle: {
		backgroundColor: Colors.white,
		borderRadius: Dimens.borderRadiusPill,
		width: Dimens.size30,
		height: Dimens.size30,
		justifyContent: 'center',
		alignItems: 'center',
		alignSelf: 'center',
	},
});

export default OrderBathCardDialog;
